import { FC, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Box, Container, Grid, Paper } from '@mui/material';
import OrderEntryTopNav from 'src/components/order/orderEntry/OrderEntryTopNav';
import StepperComponent from 'src/components/StepperComponent';
import useSettings from '../../hooks/useSettings';
import useOrderSteps from 'src/hooks/useOrderSteps';
import useOrder from 'src/hooks/useOrder';
import useCustomer from 'src/hooks/useCustomer';
import '../../theme/partials/order/OrderFlow.css';
import '../../theme/partials/order/OrderEntry.css';
import '../../theme/partials/order/ItemEdit.css';
import '../../theme/partials/order/OrderItemGrid.css';
import { getSettingsWidth } from 'src/utils/settingsHelper';
import Alert from '@mui/material/Alert';
import OrderSummaryWidget from './OrderSummaryWidget';

const OrderFlowLayout: FC = () => {
  const contentWidth = useSettings((state) => state.settings?.contentWidth);
  const showOrderSummaryWidget = useSettings((state) => state.settings?.showOrderSummaryWidget);
  const saveShowOrderSummaryWidget = useSettings((state) => state.saveShowOrderSummaryWidget);

  // Get individual values instead of objects to avoid reference changes
  const orderSteps = useOrderSteps((state) => state.orderSteps);
  const currentOrderStep = useOrderSteps((state) => state.currentOrderStep);
  const setCurrentOrderStep = useOrderSteps((state) => state.setCurrentOrderStep);

  const validateOrderHeader = useOrder((state) => state.validateOrderHeader);
  const saveOrderHeader = useOrder((state) => state.saveOrderHeader);

  const navigate = useNavigate();
  const poRequired = useCustomer((state) => state.customer.poRequired);

  const isOpenOrderEdit = useOrder((state) => state.order?.isOpenOrderEdit);
  const userEditing = useOrder((state) => state.order.userEditing);

  // Simplify handlers - remove problematic dependencies
  const handleOnStepClicked = useCallback(
    async (stepIndex: number) => {
      if (currentOrderStep === 3) {
        const isSaved = await saveOrderHeader();
        if (!isSaved) {
          console.error('Failed to save order header');
          return;
        }
      }
      setCurrentOrderStep(stepIndex);
      navigate(orderSteps.find((x) => x.step === stepIndex)?.path || '/');
    },
    [currentOrderStep, saveOrderHeader, setCurrentOrderStep, orderSteps]
  );

  const handleNextStepClicked = useCallback(async () => {
    if (currentOrderStep === 3) {
      const isValid = validateOrderHeader(poRequired);
      if (isValid) {
        const isSaved = await saveOrderHeader();
        if (!isSaved) {
          console.error('Failed to save order header');
          return;
        }
      } else {
        console.warn('Order header validation failed');
        return;
      }
    }
    const nextStep = currentOrderStep + 1;
    setCurrentOrderStep(nextStep);
    navigate(orderSteps.find((x) => x.step === nextStep)?.path || '/');
  }, [validateOrderHeader, saveOrderHeader, poRequired, currentOrderStep, setCurrentOrderStep, orderSteps]);

  const handlePreviousStepClicked = useCallback(async () => {
    console.log('handlePreviousStepClicked called currentStep:', currentOrderStep);
    if (currentOrderStep === 3) {
      const isSaved = await saveOrderHeader();
      if (!isSaved) {
        console.error('Failed to save order header');
        return;
      }
    }
    const prevStep = currentOrderStep - 1;

    console.log('Navigating to previous step:', prevStep);

    setCurrentOrderStep(prevStep);
    navigate(orderSteps.find((x) => x.step === prevStep)?.path || '/');
  }, [saveOrderHeader, currentOrderStep, setCurrentOrderStep, navigate, orderSteps]);

  return (
    <div className="order-flow">
      {isOpenOrderEdit && userEditing && userEditing.trim() && (
        <Alert severity="warning" className="user-editing-alert">
          User: <strong>{userEditing}</strong> is currently editing this order...
        </Alert>
      )}

      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 2,
        }}
      >
        <Container maxWidth={getSettingsWidth(contentWidth)} className={`container-width__${contentWidth}`}>
          <Box sx={{ pt: 1 }}>
            <OrderEntryTopNav />
          </Box>

          <OrderSummaryWidget open={showOrderSummaryWidget} onClose={() => saveShowOrderSummaryWidget(false)} onOpen={() => saveShowOrderSummaryWidget(true)} />
            
          <Outlet />
          
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10, borderRadius: '0' }} elevation={3}>
              <StepperComponent onStepClicked={(item) => handleOnStepClicked(item.step)} onNextStepClicked={handleNextStepClicked} onPrevStepClicked={handlePreviousStepClicked} />
            </Paper>
          </Grid>
        </Container>
      </Box>
    </div>
  );
};

export default OrderFlowLayout;
